import React from "react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

function rsvpUrl(slug: string): string {
  return `/events/${slug}`;
}
import Navbar from "../components/Navbar";
import MeshField from "../components/story/MeshField";
import Doodle from "../components/story/Doodle";
import Highlighter from "../components/story/Highlighter";
import Reveal from "../components/story/Reveal";
import {
  PaperGrain,
  SectionLabel,
  Polaroid,
  RsvpButton,
  PromiseDoodle,
  handwritten,
} from "../components/editorial";
import { linkify } from "../lib/linkify";
import { captureConversion } from "../lib/analytics";
import { DOWNLOAD_PATH, WHATSAPP_COMMUNITY_URL } from "../lib/appLinks";

// Public event shape returned by /api/events (public-safe fields only).
interface PublicEvent {
  id: string;
  kind: string;
  title: string;
  slug: string;
  neighbourhood: string | null;
  subtitle: string | null;
  body: string | null;
  image_url: string | null;
  starts_at: string | null;
  time_label: string | null;
  ends_note: string | null;
  meeting_point: string | null;
  route_note: string | null;
  location_address: string | null;
  host_name: string | null;
  is_free: boolean | null;
  luma_url: string | null;
  meetup_url: string | null;
  eventbrite_url: string | null;
  whatsapp_url: string | null;
  is_pinned: boolean | null;
  rsvp_enabled: boolean | null;
}

function formatEventDate(startsAt: string | null): string {
  if (!startsAt) return "Date coming soon";
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "Date coming soon";
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function formatEventTime(
  startsAt: string | null,
  timeLabel: string | null,
): string {
  if (timeLabel) return timeLabel;
  if (!startsAt) return "";
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// Derive a human event-type label from the DB `kind` plus title keywords, so a
// volleyball or picnic never gets read as a walk (kind is often the catch-all
// "other"). Rendered as a small salmon pill on each card.
function eventType(ev: { kind: string | null; title: string | null }): string {
  const k = (ev.kind || "").toLowerCase();
  const t = (ev.title || "").toLowerCase();
  const has = (s: string) => t.includes(s);
  if (has("volleyball")) return "Volleyball";
  if (has("run")) return "Run club";
  if (has("picnic")) return "Picnic";
  if (has("pub") || has("drinks")) return "Pub night";
  if (has("coffee")) return "Coffee";
  if (has("cinema") || has("film")) return "Film night";
  if (k === "walk" || has("walk")) return "Walk";
  return "Meetup";
}

const PROMISES = [
  {
    title: "Free to show up.",
    body: "Just turn up and join in.",
  },
  {
    title: "Hosted by a real person.",
    body: "Joshua's at the front. Says hello to every new face. Stays until the last person leaves.",
  },
  {
    title: "Solo or +1.",
    body: "If you're nervous, bring a friend. Most people come alone. Couples come too.",
  },
  {
    title: "No icebreakers, ever.",
    body: 'No name games. No "go round the circle." The thing you came for sorts the conversation out by itself.',
  },
];

const FAQS = [
  {
    q: "What kinds of events are these?",
    a: "Walks, volleyball, picnics, pub nights, run clubs, and more. Usually a different thing each week, all free, all in London to start. The map in the app has thousands more community events across the UK.",
  },
  {
    q: "Do I have to RSVP?",
    a: "Helps us know roughly how many to expect, but we won't turn anyone away. Show up if you can.",
  },
  {
    q: "What if I don't know anyone?",
    a: "Most people don't. That's the point. Joshua greets every new face at the meeting point. Find him first.",
  },
  {
    q: "What if it rains?",
    a: "Light rain we still go. Heavy rain, we move somewhere dry or reschedule. Either way, we don't cancel without telling you on WhatsApp a couple of hours before.",
  },
  {
    q: "Are these singles events?",
    a: "No. People come solo, in pairs, as couples, with friends. Being around good company is the point: the rest is your business.",
  },
  {
    q: "How long do events run?",
    a: "Depends on the format. Walks are about 90 minutes. Volleyball and picnics run a couple of hours. The end time's always on the event page.",
  },
];

const POLAROIDS = [
  {
    src: "/polaroid-hackney.webp",
    rotate: "-rotate-3",
    caption: "Hackney, last winter",
  },
  { src: "/polaroid-pub.webp", rotate: "rotate-2", caption: "Walk → pub" },
  {
    src: "/polaroid-sunday.webp",
    rotate: "-rotate-1",
    caption: "Sunday people",
  },
];

const COMMUNITY_AVATARS = [
  "emma",
  "finn",
  "leo",
  "omar",
  "priya",
  "raj",
  "sarah",
  "zara",
];

// ─── Explore-the-map filter data (drives the app-download conversion band) ──
// LetsLoop hosts the events listed above; the app's map carries 1,000+ more
// community events. This filter is a genuine *preview*: London is where we
// host and where the map is densest; other UK cities are on the map with
// hosted meetups following; other countries are honestly "not yet". Selecting
// anything routes to the app — the full live listings live there, never on web.
type CityFilter = {
  name: string;
  status: "host" | "map" | "soon";
  tags: string[];
};

const UK_CITIES: CityFilter[] = [
  {
    name: "London",
    status: "host",
    tags: [
      "Walks",
      "Volleyball",
      "Run clubs",
      "Live music",
      "Board games",
      "Pub nights",
      "Markets",
    ],
  },
  {
    name: "Manchester",
    status: "map",
    tags: ["Live music", "Walks", "Markets", "Drinks"],
  },
  {
    name: "Bristol",
    status: "map",
    tags: ["Board games", "Live music", "Coffee", "Art"],
  },
  {
    name: "Oxford",
    status: "map",
    tags: ["Books", "Coffee", "Walks", "Live music"],
  },
  { name: "Brighton", status: "soon", tags: [] },
  { name: "Leeds", status: "soon", tags: [] },
  { name: "Edinburgh", status: "soon", tags: [] },
];

const COUNTRIES: { code: string; name: string; live: boolean }[] = [
  { code: "uk", name: "United Kingdom", live: true },
  { code: "ie", name: "Ireland", live: false },
  { code: "fr", name: "France", live: false },
  { code: "es", name: "Spain", live: false },
  { code: "us", name: "United States", live: false },
];

// One event rendered in the editorial language. `featured` = the large
// hero-card styling used for the first/pinned event; the rest stack smaller.
function EventCard({
  event,
  featured,
}: {
  event: PublicEvent;
  featured: boolean;
}) {
  const date = formatEventDate(event.starts_at);
  const time = formatEventTime(event.starts_at, event.time_label);
  const type = eventType(event);
  const hasExternal =
    !!event.luma_url ||
    !!event.meetup_url ||
    !!event.eventbrite_url ||
    !!event.whatsapp_url;

  return (
    <div
      className={
        featured
          ? "bg-canvas border border-ink/15 rounded-3xl p-8 md:p-10 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] relative overflow-hidden"
          : "bg-canvas border-2 border-ink/15 rounded-3xl p-7 md:p-8 relative overflow-hidden hover:border-ink/40 transition-colors"
      }
    >
      {featured && (
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-salmon rounded-full opacity-20" />
      )}
      <div className="relative">
        {/* Event-type pill + free tag — makes the format unmistakable at a glance */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-salmon border border-salmon/50 rounded-full px-3 py-1">
            {type}
          </span>
          {event.is_free !== false && (
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50 border border-ink/15 rounded-full px-3 py-1">
              Free
            </span>
          )}
        </div>
        <p
          className={
            featured ? "text-2xl text-salmon mb-2" : "text-xl text-salmon mb-1"
          }
          style={handwritten}
        >
          {event.neighbourhood || "London"} calling →
        </p>
        <h3
          className={
            featured
              ? "font-serif text-4xl md:text-5xl tracking-tight leading-tight mb-2"
              : "font-serif text-2xl md:text-3xl tracking-tight leading-tight mb-2"
          }
        >
          {event.title}
        </h3>
        <p
          className={
            featured
              ? "font-serif text-2xl md:text-3xl italic text-gray-600 mb-6"
              : "font-serif text-lg md:text-xl italic text-gray-600 mb-5"
          }
        >
          {date}
          {time ? ` · ${time}` : ""}
        </p>

        {event.subtitle && (
          <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
            {event.subtitle}
          </p>
        )}

        <dl className="space-y-4 mb-8">
          {event.meeting_point && (
            <div>
              <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                Meet at
              </dt>
              <dd className="text-lg">{linkify(event.meeting_point)}</dd>
            </div>
          )}
          {event.route_note && (
            <div>
              <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                The route
              </dt>
              <dd className="text-lg leading-relaxed">
                {linkify(event.route_note)}
              </dd>
            </div>
          )}
          {event.ends_note && (
            <div>
              <dt className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
                Ends around
              </dt>
              <dd className="text-lg">{event.ends_note}</dd>
            </div>
          )}
        </dl>

        {(event.rsvp_enabled || hasExternal) && (
          <div className="space-y-3">
            {event.rsvp_enabled && (
              <>
                <a
                  href={rsvpUrl(event.slug)}
                  className="w-full rounded-xl px-5 py-4 transition-all flex items-center justify-center gap-2 font-semibold text-base bg-salmon text-on-salmon hover:bg-ink hover:text-canvas border border-ink/15"
                >
                  <span>RSVP here</span>
                  <span aria-hidden>→</span>
                </a>
                <p className="text-center text-sm text-gray-500">
                  Free · solo welcome · takes 20 seconds
                </p>
              </>
            )}

            {event.luma_url && (
              <RsvpButton
                url={event.luma_url}
                label="RSVP on Lu.ma"
                sub="external"
                variant={event.rsvp_enabled ? "ghost" : "primary"}
              />
            )}
            {(event.meetup_url || event.eventbrite_url || event.whatsapp_url) &&
              (() => {
                // Match column count to actual button count so a lone
                // Meetup button doesn't sit at 1/3 width.
                const count = [
                  event.meetup_url,
                  event.eventbrite_url,
                  event.whatsapp_url,
                ].filter(Boolean).length;
                const cols =
                  count === 1
                    ? "grid-cols-1"
                    : count === 2
                      ? "grid-cols-1 sm:grid-cols-2"
                      : "grid-cols-1 sm:grid-cols-3";
                return (
                  <div className={`grid ${cols} gap-3`}>
                    {event.meetup_url && (
                      <RsvpButton
                        url={event.meetup_url}
                        label="Meetup"
                        variant="ghost"
                      />
                    )}
                    {event.eventbrite_url && (
                      <RsvpButton
                        url={event.eventbrite_url}
                        label="Eventbrite"
                        variant="ghost"
                      />
                    )}
                    {event.whatsapp_url && (
                      <RsvpButton
                        url={event.whatsapp_url}
                        label="WhatsApp"
                        variant="ghost"
                      />
                    )}
                  </div>
                );
              })()}
          </div>
        )}
      </div>
    </div>
  );
}

// The app-download conversion band: pick a country → city and see what the map
// carries there, with a single "open it in the app" action. Honest by design —
// no fabricated counts, no scraped rows on web; the real listings live in-app.
function ExploreMap() {
  const [countryCode, setCountryCode] = useState("uk");
  const [cityName, setCityName] = useState("London");

  const country = COUNTRIES.find((c) => c.code === countryCode) ?? COUNTRIES[0];
  const city =
    countryCode === "uk"
      ? (UK_CITIES.find((c) => c.name === cityName) ?? UK_CITIES[0])
      : null;

  const statusLine = (c: CityFilter): string => {
    if (c.status === "host")
      return "We host events here every week, and the map is densest here.";
    if (c.status === "map")
      return "Already on the map. Community events tracked here now, LetsLoop-hosted meetups following.";
    return "Not on the map yet. Get the app and you'll be the first to know when we arrive.";
  };

  return (
    <div className="bg-canvas border-2 border-ink/10 rounded-3xl p-6 md:p-10 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)]">
      {/* Country row */}
      <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
        Country
      </p>
      <div className="flex flex-wrap gap-2 mb-8">
        {COUNTRIES.map((c) => {
          const active = c.code === countryCode;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => setCountryCode(c.code)}
              className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                active
                  ? "bg-ink text-canvas border-ink"
                  : "bg-canvas text-ink/70 border-ink/15 hover:border-ink/40"
              }`}
            >
              {c.name}
              {!c.live && (
                <span className="ml-1.5 text-[10px] uppercase tracking-widest opacity-60">
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* City row — only for the live country (UK) */}
      {country.live && (
        <>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
            City
          </p>
          <div className="flex flex-wrap gap-2 mb-8">
            {UK_CITIES.map((c) => {
              const active = c.name === cityName;
              return (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setCityName(c.name)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${
                    active
                      ? "bg-salmon text-on-salmon border-ink/15"
                      : "bg-canvas text-ink/70 border-ink/15 hover:border-ink/40"
                  }`}
                >
                  {c.name}
                  {c.status === "soon" && (
                    <span className="ml-1.5 text-[10px] uppercase tracking-widest opacity-60">
                      soon
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Preview panel */}
      <div
        className="rounded-2xl p-6 md:p-8 border-2 border-ink/10"
        style={{ backgroundColor: "var(--color-paper-salmon)" }}
      >
        {country.live && city ? (
          <>
            <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
                {city.name}
              </h3>
              {city.status === "host" && (
                <span className="text-xl text-salmon" style={handwritten}>
                  we're live here →
                </span>
              )}
            </div>
            <p className="text-base text-gray-700 leading-relaxed mb-5">
              {statusLine(city)}
            </p>
            {city.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {city.tags.map((t) => (
                  <span
                    key={t}
                    className="text-xs font-semibold uppercase tracking-widest text-ink/60 bg-canvas border border-ink/10 rounded-full px-3 py-1.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-2">
              {country.name}
            </h3>
            <p className="text-base text-gray-700 leading-relaxed mb-6">
              We're London-first for now. The app is how you'll hear the moment
              LetsLoop reaches {country.name}.
            </p>
          </>
        )}

        <Link
          to={DOWNLOAD_PATH}
          onClick={() =>
            captureConversion("events_explore_app_click", {
              country: country.code,
              city: country.live && city ? city.name : "",
            })
          }
          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-salmon text-on-salmon font-semibold py-3.5 px-6 rounded-xl hover:bg-ink hover:text-canvas transition-colors text-base"
        >
          {country.live && city
            ? `See what's on in ${city.name} in the app`
            : "Get the app"}
          <span aria-hidden>→</span>
        </Link>
        <p className="text-xs text-gray-500 mt-3">
          Full live listings live in the app. iOS &amp; Android.
        </p>
      </div>
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  // C-1 (LL-0028): explicit email-marketing opt-in, unticked by default.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitState, setSubmitState] = useState<
    "idle" | "loading" | "done" | "error"
  >("idle");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [eventsState, setEventsState] = useState<"loading" | "ready" | "empty">(
    "loading",
  );

  useEffect(() => {
    document.title =
      "Free community events in London: walks, volleyball, picnics, pub nights | LetsLoop";
  }, []);

  // Client-side fetch only — keeps the prerendered shell static (no
  // build-time Supabase call). Any failure falls back to the empty state
  // (the interest form), so the page never breaks on a bad/missing API.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/events")
      .then(async (res) => {
        if (!res.ok) throw new Error("events fetch failed");
        const data = (await res.json()) as { events?: PublicEvent[] };
        if (cancelled) return;
        const list = Array.isArray(data.events) ? data.events : [];
        setEvents(list);
        setEventsState(list.length > 0 ? "ready" : "empty");
      })
      .catch(() => {
        if (!cancelled) setEventsState("empty");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submitInterest = async (e: FormEvent) => {
    e.preventDefault();
    // Require the explicit email-marketing opt-in (C-1): no consent, no submit.
    if (!email.trim() || !marketingConsent) return;
    setSubmitState("loading");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          seekerType: "events",
          interest: "community_events",
          // C-1: explicit opt-in + the form it came from, recorded server-side.
          marketingConsent: true,
          sourceForm: "events_interest",
          // Honeypot — real users never fill this (it's visually hidden).
          company: honeypot,
        }),
      });
      if (!res.ok) throw new Error("subscribe failed");
      setSubmitState("done");
      setEmail("");
      setMarketingConsent(false);
      // GA4 conversion event (LL-0024). Consent gating lives inside
      // captureConversion -- no-op until the analytics category is granted.
      captureConversion("waitlist_signup", {
        method: "events_interest_form",
        interest: "community_events",
      });
    } catch {
      setSubmitState("error");
    }
  };

  return (
    <>
      {/* `onOpenSignup` was passed here but Navbar has never declared it, so it
          was silently dropped at runtime and `npm run typecheck` has been RED
          on main since the story-site redesign. Removed rather than added to
          NavbarProps: nothing read it, so adding it would be inventing a
          feature to satisfy a compiler. */}
      <Navbar onNavigate={() => navigate("/")} currentPage="events" />
      <main
        className="min-h-screen text-ink"
        style={{ backgroundColor: "var(--color-paper)" }}
      >
        {/* Paper grain overlay — site-wide for this page */}
        <PaperGrain />

        {/* ─── HERO ──────────────────────────────────────────────────── */}
        <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 px-4 md:px-6 overflow-hidden">
          <MeshField variant="ambient" />
          <div aria-hidden="true" className="absolute inset-0">
            <Doodle
              name="star"
              size={42}
              rotate={-14}
              className="absolute left-[3%] top-[22%] text-salmon-dark/50 story-float"
            />
            <Doodle
              name="squiggle"
              size={84}
              className="absolute right-[5%] top-[16%] text-teal/35 story-float"
              style={{ animationDelay: "-2.6s" }}
            />
            <Doodle
              name="burst"
              size={36}
              className="absolute left-[8%] bottom-[12%] text-salmon/60 story-float"
              style={{ animationDelay: "-1.4s" }}
            />
          </div>
          <div className="max-w-6xl mx-auto relative">
            {/* Handwritten kicker */}
            <p
              className="story-enter text-2xl md:text-3xl text-salmon-dark mb-4"
              style={handwritten}
            >
              &rarr; Something&apos;s always happening,{" "}
              <span className="text-ink">near you.</span>
            </p>
            <h1
              className="story-enter font-headline text-ink tracking-[-0.03em] leading-[1.06] text-[clamp(2.4rem,7vw,4.6rem)] mb-6 text-balance"
              style={
                {
                  fontWeight: 700,
                  "--enter-delay": "110ms",
                } as React.CSSProperties
              }
            >
              Free things to do in London,{" "}
              <Highlighter tone="gold">with people.</Highlighter>
            </h1>

            <p
              className="story-enter text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl mb-12"
              style={{ "--enter-delay": "240ms" } as React.CSSProperties}
            >
              Free community events across London. Walks, volleyball, pub
              nights, picnics. Come on your own, leave with people. No pressure,
              no icebreaker games, just good company and something to do.
            </p>

            {/* Hero photo collage */}
            <div
              className="story-enter relative h-[400px] md:h-[500px]"
              style={{ "--enter-delay": "360ms" } as React.CSSProperties}
            >
              {/* Main photo */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/events-friends.png"
                  alt="Friends out together in London"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-ink/30 via-transparent to-transparent" />
              </div>

              {/* Polaroid overlay top-right */}
              <div className="absolute w-8 h-8 md:w-auto md:h-auto -top-6 -right-4 rotate-6 transform-gpu">
                <Polaroid
                  src="/polaroid-sunday.webp"
                  caption="last Sunday"
                  size="sm"
                />
              </div>

              {/* Date stamp overlay bottom-left — reflects the next live event */}
              <div className="absolute -bottom-6 left-4 md:left-8 bg-canvas border border-ink/15 px-5 py-3 -rotate-3 shadow-xl">
                <p
                  className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-0.5"
                  style={{ letterSpacing: "0.18em" }}
                >
                  Next up
                </p>
                <p className="font-serif text-xl md:text-2xl">
                  {eventsState === "ready" && events[0]
                    ? `${formatEventDate(events[0].starts_at)}${
                        formatEventTime(
                          events[0].starts_at,
                          events[0].time_label,
                        )
                          ? ` · ${formatEventTime(
                              events[0].starts_at,
                              events[0].time_label,
                            )}`
                          : ""
                      }`
                    : "Coming soon"}
                </p>
              </div>

              {/* Floating tag bottom-right */}
              <div
                className="absolute w-8 h-8 md:w-auto md:h-auto bottom-6 right-6 text-3xl text-canvas"
                style={handwritten}
              >
                free · solo or +1 · all welcome
              </div>
            </div>
          </div>
        </section>

        {/* ─── WHAT WE'RE HOSTING (live event list) ─────────────────── */}
        <section className="ground-butter px-4 md:px-6 py-20 md:py-32 relative overflow-hidden">
          <Doodle
            name="loop"
            size={74}
            rotate={9}
            className="absolute w-8 h-8 md:w-auto md:h-auto right-[4%] max-md:right-[5%] top-[8%] text-teal/25"
          />
          <div className="max-w-5xl mx-auto">
            <SectionLabel kicker="01" title="What we're hosting" />
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl -mt-2 mb-10">
              The events we run ourselves. Free, in London, and you can RSVP
              right here in a few taps.
            </p>

            {/* Loading — editorial skeleton */}
            {eventsState === "loading" && (
              <div className="animate-pulse">
                <div className="bg-canvas border-2 border-ink/10 rounded-3xl p-8 md:p-10">
                  <div className="h-5 w-32 bg-ink/10 rounded mb-4" />
                  <div className="h-10 w-3/4 bg-ink/10 rounded mb-3" />
                  <div className="h-7 w-1/2 bg-ink/10 rounded mb-8" />
                  <div className="space-y-3">
                    <div className="h-5 w-2/3 bg-ink/10 rounded" />
                    <div className="h-5 w-1/2 bg-ink/10 rounded" />
                    <div className="h-5 w-3/5 bg-ink/10 rounded" />
                  </div>
                  <div className="h-14 w-full bg-ink/10 rounded-xl mt-8" />
                </div>
              </div>
            )}

            {/* Ready — first/pinned = featured hero card, rest stacked */}
            {eventsState === "ready" && (
              <div className="space-y-8">
                {events.map((ev, i) => (
                  <Reveal key={ev.id} y={24} delay={Math.min(i, 4) * 60}>
                    <EventCard event={ev} featured={i === 0} />
                  </Reveal>
                ))}
              </div>
            )}

            {/* Empty — the existing interest form + community proof */}
            {eventsState === "empty" && (
              <div className="grid md:grid-cols-5 gap-8 items-start">
                <div className="md:col-span-3 relative">
                  <div className="bg-canvas border border-ink/15 rounded-3xl p-8 md:p-10 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] relative overflow-hidden">
                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-salmon rounded-full opacity-20" />
                    <div className="relative">
                      <p
                        className="text-2xl text-salmon mb-2"
                        style={handwritten}
                      >
                        between events →
                      </p>
                      <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight mb-4">
                        Nothing on the calendar
                        <br />
                        <span className="italic">just yet.</span>
                      </h3>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                        We run events across London most weeks. The next one's
                        being planned now. Drop your email and you&apos;ll be
                        first to know the time and place.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 flex flex-col gap-6">
                  <div
                    className="rounded-3xl p-7 md:p-8 border-2 border-ink/10"
                    style={{ backgroundColor: "var(--color-paper-salmon)" }}
                  >
                    <p className="text-2xl text-ink mb-2" style={handwritten}>
                      get notified
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-5">
                      We'll email you when the next event goes live. One email
                      per event. Unsubscribe any time.
                    </p>

                    {submitState === "done" ? (
                      <p className="text-base font-semibold text-ink py-3 px-4 bg-canvas rounded-xl border-2 border-ink/10">
                        On the list. See you soon.
                      </p>
                    ) : (
                      <form onSubmit={submitInterest} className="space-y-3">
                        {/* Honeypot — visually hidden, off-screen, not tab-focusable */}
                        <div
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            left: "-9999px",
                            width: "1px",
                            height: "1px",
                            overflow: "hidden",
                          }}
                        >
                          <label htmlFor="newsletter-company">
                            Company (leave this empty)
                          </label>
                          <input
                            id="newsletter-company"
                            name="company"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={honeypot}
                            onChange={(e) => setHoneypot(e.target.value)}
                          />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@hackney.com"
                          required
                          className="w-full px-4 py-3 rounded-xl border-2 border-ink/15 bg-canvas focus:outline-none focus:border-ink/40 transition-colors text-base"
                        />
                        {/* C-1: explicit, unticked email-marketing opt-in.
                            Required to submit; recorded server-side. */}
                        <label className="flex items-start gap-2.5 text-sm text-gray-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={marketingConsent}
                            onChange={(e) =>
                              setMarketingConsent(e.target.checked)
                            }
                            required
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-ink/25 accent-salmon focus:outline-none focus:ring-2 focus:ring-salmon"
                          />
                          <span>
                            Email me about LetsLoop events and launch updates.
                            You can unsubscribe anytime.
                          </span>
                        </label>
                        <button
                          type="submit"
                          disabled={
                            submitState === "loading" || !marketingConsent
                          }
                          className="w-full bg-ink text-canvas font-semibold py-3 px-4 rounded-xl hover:bg-salmon hover:text-ink transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {submitState === "loading"
                            ? "Adding..."
                            : "Notify me →"}
                        </button>
                        {submitState === "error" && (
                          <p className="text-xs text-red-600">
                            Something broke. Try again or DM us on Instagram.
                          </p>
                        )}
                      </form>
                    )}
                  </div>

                  {/* Community proof */}
                  <div className="bg-canvas border-2 border-ink/10 rounded-3xl p-6">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                      Who comes
                    </p>
                    <div className="flex -space-x-2 mb-3">
                      {COMMUNITY_AVATARS.slice(0, 6).map((name) => (
                        <img
                          key={name}
                          src={`/mock-avatars/${name}.jpg`}
                          alt=""
                          className="w-10 h-10 rounded-full border-2 border-canvas object-cover"
                        />
                      ))}
                      <div className="w-10 h-10 rounded-full border-2 border-canvas bg-salmon flex items-center justify-center text-canvas text-xs font-bold">
                        +
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Mid-20s through mid-30s. Mostly people who came on their
                      own too.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ─── EXPLORE THE MAP (1,000+ community events → app) ──────── */}
        <section
          className="px-4 md:px-6 py-20 md:py-32 relative"
          style={{ backgroundColor: "var(--color-paper-tan)" }}
        >
          <div className="max-w-5xl mx-auto">
            <SectionLabel kicker="02" title="The rest is on the map" />
            <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl -mt-2 mb-4">
              We host the events above. LetsLoop also tracks{" "}
              <span className="font-semibold text-ink">
                1,000+ more community events
              </span>{" "}
              happening across the UK. Walks, run clubs, live music, board game
              nights, markets, and plenty more. Pick a place to see what&apos;s
              on.
            </p>
            <p className="text-2xl text-salmon mb-10" style={handwritten}>
              London and dozens of UK cities, growing every week →
            </p>

            <ExploreMap />
          </div>
        </section>

        {/* ─── WHAT IT LOOKS LIKE (polaroid gallery) ───────────────── */}
        <section className="px-4 md:px-6 py-20 md:py-32 relative">
          <div className="max-w-6xl mx-auto">
            <SectionLabel kicker="03" title="What it actually looks like" />

            <div className="grid md:grid-cols-3 gap-12 md:gap-8 mt-12">
              {POLAROIDS.map((p, i) => (
                // The tilt survives sitting on the same element as the reveal:
                // Tailwind v4 compiles `-rotate-3` to the independent `rotate`
                // property, which composes with the `transform` the reveal
                // animates rather than being overwritten by it. (Verified in
                // the built CSS: `.-rotate-3{rotate:-3deg}`.)
                <Reveal
                  key={p.src}
                  y={30}
                  delay={i * 100}
                  className={`${p.rotate} transform-gpu`}
                >
                  <Polaroid src={p.src} caption={p.caption} />
                </Reveal>
              ))}
            </div>

            <p
              className="text-2xl text-center mt-16 text-gray-600 max-w-2xl mx-auto"
              style={handwritten}
            >
              Mostly: people showing up on their own, talking sideways, ending
              up somewhere they didn't expect.
            </p>
          </div>
        </section>

        {/* ─── THE FORMAT (4 promises) ────────────────────────────── */}
        <section
          className="px-4 md:px-6 py-20 md:py-32 relative"
          style={{ backgroundColor: "var(--color-paper-tan)" }}
        >
          <div className="max-w-5xl mx-auto">
            <SectionLabel kicker="04" title="How it works" />

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-14 mt-12">
              {PROMISES.map((p, i) => (
                <Reveal
                  key={p.title}
                  y={20}
                  delay={i * 80}
                  className="relative"
                >
                  <div className="flex items-start gap-4">
                    <PromiseDoodle index={i} />
                    <div className="flex-1">
                      <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3">
                        {p.title}
                      </h3>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                        {p.body}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── MEET YOUR HOST ──────────────────────────────────────── */}
        <section
          className="px-4 md:px-6 py-20 md:py-32 relative"
          style={{ backgroundColor: "var(--color-paper-salmon)" }}
        >
          <div className="max-w-5xl mx-auto">
            <SectionLabel kicker="05" title="Hosted by" />

            <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-center mt-12">
              <div className="md:col-span-2">
                <div className="relative inline-block">
                  <Polaroid
                    src="/josh-founder.jpg"
                    caption="Joshua · LetsLoop"
                    size="lg"
                  />
                  <div
                    className="absolute -top-8 -right-6 text-3xl text-salmon -rotate-12 hidden md:block"
                    style={handwritten}
                  >
                    your host
                  </div>
                </div>
              </div>
              <div className="md:col-span-3">
                <p
                  className="text-3xl md:text-4xl text-gray-500 mb-2"
                  style={handwritten}
                >
                  "
                </p>
                <p className="font-serif text-2xl md:text-3xl leading-snug tracking-tight mb-6">
                  I started LetsLoop because once you&rsquo;re an adult, a city
                  stops introducing you to the same people twice. Then I noticed
                  the connections that stuck happened in real life, not on a
                  screen.
                </p>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6">
                  So now we show up. Walks, volleyball, picnics, pub nights. New
                  face every week, same easy welcome. If you've ever wanted to
                  know your neighbours, this is the lowest-effort way I can
                  think of.
                </p>
                <p className="text-sm font-semibold uppercase tracking-widest text-ink">
                  Joshua, founder, LetsLoop
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── STAY IN THE LOOP ────────────────────────────────────── */}
        <section className="px-4 md:px-6 py-20 md:py-32 relative">
          <div className="max-w-5xl mx-auto">
            <SectionLabel kicker="06" title="Stay in the loop" />

            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <a
                href="https://www.instagram.com/letsloop.app"
                target="_blank"
                rel="noreferrer"
                className="group bg-salmon text-on-salmon rounded-3xl p-8 md:p-10 hover:bg-ink hover:text-canvas transition-colors block"
              >
                <p className="text-2xl mb-3 opacity-70" style={handwritten}>
                  follow →
                </p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">
                  @letsloop.app
                </h3>
                <p className="text-base opacity-80 leading-relaxed">
                  Event photos, neighbourhood drops, the next time + place. The
                  first place we post.
                </p>
              </a>

              <a
                href={WHATSAPP_COMMUNITY_URL}
                target="_blank"
                rel="noreferrer"
                className="group block rounded-3xl p-8 md:p-10 border-2 border-ink/10 hover:border-emerald-600/50 transition-colors"
                style={{ backgroundColor: "var(--color-paper-mint)" }}
              >
                <p
                  className="text-2xl mb-3 text-emerald-700"
                  style={handwritten}
                >
                  join →
                </p>
                <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">
                  WhatsApp community
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-5">
                  Where regulars chat between events, share what&apos;s on, and
                  sort out who&apos;s going. Totally optional, leave any time.
                </p>
                <p className="text-sm font-semibold text-emerald-800">
                  Tap to join the community →
                </p>
              </a>
            </div>
          </div>
        </section>

        {/* ─── FAQ ─────────────────────────────────────────────────── */}
        <section className="px-4 md:px-6 py-20 md:py-32 relative">
          <div className="max-w-3xl mx-auto">
            <SectionLabel kicker="07" title="Questions" />

            <div className="mt-12 space-y-3">
              {FAQS.map((f, i) => (
                <div key={f.q} className="border-b-2 border-ink/10 pb-3">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full text-left flex justify-between items-center gap-4 py-4 group"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-serif text-xl md:text-2xl tracking-tight text-ink group-hover:text-salmon transition-colors">
                      {f.q}
                    </span>
                    <span
                      className={`text-3xl text-salmon transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""}`}
                      aria-hidden
                    >
                      +
                    </span>
                  </button>
                  {openFaq === i && (
                    // Not a Reveal: this opens on click, it is not scrolled
                    // into view. A CSS mount animation gated on `.js` keeps the
                    // feel while leaving the first answer (open by default, so
                    // it IS in the prerendered HTML) plainly visible without
                    // JS. The old motion.p shipped `opacity:0;height:0px` into
                    // that static markup. Its `exit` never ran either: there is
                    // no AnimatePresence here, the element just unmounts.
                    <p className="faq-answer text-base md:text-lg text-gray-600 leading-relaxed pb-4 pr-12">
                      {f.a}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CODE OF CONDUCT ─────────────────────────────────────── */}
        <section
          id="code-of-conduct"
          className="px-4 md:px-6 py-20 md:py-32 relative"
          style={{ backgroundColor: "var(--color-paper-tan)" }}
        >
          <div className="max-w-3xl mx-auto">
            <SectionLabel kicker="08" title="Code of conduct" />

            <div className="mt-12 space-y-6 text-lg leading-relaxed text-gray-700">
              <p>
                <span className="font-serif italic text-ink">
                  The simple version.
                </span>{" "}
                Be a decent person. Don't pressure anyone for their number,
                social, or contact info. Don't take photos of people without
                checking. If you're drunk, don't be the loudest one in the pub.
                If someone says they need space, give them space.
              </p>
              <p>
                <span className="font-serif italic text-ink">
                  If someone makes you uncomfortable,
                </span>{" "}
                tell Joshua. We'll handle it. They won't be welcome at future
                events.
              </p>
              <p>
                <span className="font-serif italic text-ink">What we do:</span>{" "}
                host community events across London, film and photograph
                occasionally (opt out at sign-in), email you about future events
                only if you opt in, add you to a WhatsApp community only if you
                tick the box.
              </p>

              <p className="pt-6 text-sm text-gray-500 border-t border-ink/10">
                Joshua, LetsLoop ·{" "}
                <a
                  href="mailto:hello@letsloop.app"
                  className="text-ink font-semibold border-b border-ink hover:text-salmon hover:border-salmon transition-colors"
                >
                  hello@letsloop.app
                </a>
              </p>
            </div>
          </div>
        </section>

        {/* ─── SIGN-OFF ────────────────────────────────────────────── */}
        <section className="px-4 md:px-6 py-24 md:py-32 relative text-center">
          <div className="max-w-3xl mx-auto">
            <p className="text-3xl text-salmon mb-6" style={handwritten}>
              until then,
            </p>
            <p className="font-serif text-4xl md:text-6xl tracking-tight leading-[0.95] mb-10">
              Something&rsquo;s always{" "}
              <span className="italic text-salmon">happening near you.</span>
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <Link
                to={DOWNLOAD_PATH}
                className="inline-block bg-salmon text-on-salmon font-semibold py-3 px-6 rounded-xl hover:bg-ink hover:text-canvas transition-colors text-sm uppercase tracking-widest"
              >
                Get the app →
              </Link>
              <Link
                to="/"
                className="inline-block text-sm font-semibold uppercase tracking-widest border-b-2 border-ink pb-1 hover:text-salmon hover:border-salmon transition-colors"
              >
                ← Back to LetsLoop
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
